// WHAT: Component for displaying individual reminder item
// WHY:  Provides visual representation of reminder with completion action
// DOMAIN: Productivity → Reminders
// DEPENDENCIES: Reminder type
// RISKS:
//   - NOTE: No edit functionality - users cannot modify reminders
//   - NOTE: Date display format may not be user-friendly

import { type FC } from "react";

import { type Reminder } from "../types";

/**
 * Props for ReminderItem component
 */
interface Properties {
  /** Handler for marking reminder as done */
  onMarkDone: () => void;
  /** Reminder data to display */
  reminder: Reminder;
}

/**
 * WHAT:
 *   Component that displays individual reminder with title, description,
 *   next due date, and completion button.
 *
 * WHY:
 *   Provides consistent visual representation of reminders across the app.
 *
 * PROPS:
 *   - reminder: Reminder — Reminder data
 *   - onMarkDone: () => void — Mark as done handler
 *
 * BEHAVIOR:
 *   - Displays reminder title and description
 *   - Shows next due date if available
 *   - Shows "Done" button if not completed
 *   - Uses TracQ branding classes
 *
 * RISKS:
 *   - NOTE: No edit functionality - users cannot modify reminders
 *   - NOTE: Date display format may not be user-friendly
 *
 * @param root0
 *
 * @param root0.reminder
 *
 * @param root0.onMarkDone
 *
 * @returns JSX.Element
 *
 */
export const ReminderItem: FC<Properties> = ({ onMarkDone, reminder }) => (
  <li className="flex items-center justify-between rounded-md p-3 bg-slate-800 text-white">
    <div>
      <p className="font-brand-medium">{reminder.title}</p>
      {reminder.description ? <p className="text-sm opacity-80">{reminder.description}</p> : null}
      {reminder.next_due_date ? (
        <p className="text-xs opacity-60">Next: {reminder.next_due_date}</p>
      ) : null}
    </div>
    {!reminder.is_done && (
      <button className="btn-primary text-sm" onClick={onMarkDone}>
        Done
      </button>
    )}
  </li>
);
